import { create } from 'zustand';
import type { ToastType } from '../components/ui/Toast';
import { logger } from '../utils/logger';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  duration?: number;
  showRetry?: boolean;
  onRetry?: () => void;
  autoClose?: boolean;
}

interface AddToastParams {
  type: ToastType;
  message: string;
  title?: string;
  duration?: number;
  showRetry?: boolean;
  onRetry?: () => void;
  autoClose?: boolean;
}

interface ToastUIState {
  toasts: Toast[];
  addToast: (params: AddToastParams) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
  updateToast: (id: string, params: Partial<Omit<Toast, 'id'>>) => void;
}

export const useToastUIStore = create<ToastUIState>(set => ({
  toasts: [],

  addToast: ({ 
    type, 
    message, 
    title, 
    duration = 5000, 
    showRetry = false, 
    onRetry,
    autoClose = true 
  }: AddToastParams) => {
    const id = crypto.randomUUID();
    logger.component('Toast: Adding toast', { 
      type, 
      message,
      title, 
      id, 
      duration,
      showRetry,
      autoClose
    });
    
    set(state => ({
      toasts: [...state.toasts, { 
        id, 
        type, 
        message, 
        title,
        duration, 
        showRetry, 
        onRetry,
        autoClose 
      }],
    }));
    
    // Auto-remove after duration if specified and autoClose is true
    if (autoClose && duration > 0) {
      setTimeout(() => {
        set(state => ({
          toasts: state.toasts.filter(n => n.id !== id),
        }));
      }, duration);
    }
    
    return id;
  },

  removeToast: (id: string) => {
    logger.component('Toast: Removing toast', { id });
    set(state => ({
      toasts: state.toasts.filter(n => n.id !== id),
    }));
  },

  clearAllToasts: () => {
    logger.component('Toast: Clearing all toasts');
    set({ toasts: [] });
  },

  updateToast: (id: string, params: Partial<Omit<Toast, 'id'>>) => {
    logger.component('Toast: Updating toast', { id, ...params });
    set(state => ({
      toasts: state.toasts.map(toast => 
        toast.id === id ? { ...toast, ...params } : toast
      ),
    }));
  },
}));